
import React, { useState } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Link } from 'react-router-dom';

// Mock blog post data - in a real app, this would come from an API
const initialBlogPosts = [
  {
    id: '1',
    title: 'Starting Your SaaS Reseller Business: A Complete Guide',
    excerpt: 'Learn how to kickstart your SaaS reseller business with our comprehensive guide covering everything from licensing to marketing.',
    category: 'business',
    date: '2025-04-20',
    author: 'Sarah Johnson',
    image: '/assets/5df22fbf-46c0-422b-a964-30ad729c926d.png',
    readTime: '8 min'
  },
  {
    id: '2',
    title: 'How to Scale Your Referral Program as a Reseller',
    excerpt: 'Discover proven strategies to grow your customer base through a well-structured referral program tailored for SaaS resellers.',
    category: 'marketing',
    date: '2025-04-15',
    author: 'David Chen',
    image: '/assets/74192c5a-ba76-4e08-9c4a-f98e75942d6c.png',
    readTime: '6 min'
  },
  {
    id: '3', 
    title: 'The Future of White-Label Solutions in 2025',
    excerpt: 'Explore emerging trends in white-label software and how they are reshaping the landscape for SaaS resellers and entrepreneurs.',
    category: 'trends',
    date: '2025-04-10',
    author: 'Jessica Martinez',
    image: '/assets/843f56a9-ea90-4bfc-9000-a1b7e1408a94.png',
    readTime: '5 min'
  },
  {
    id: '4',
    title: 'Maximizing Revenue Share: Tactics for SaaS Resellers',
    excerpt: 'Learn advanced strategies to optimize your revenue streams and increase profitability as a SaaS reseller partner.',
    category: 'business',
    date: '2025-04-05',
    author: 'Sarah Johnson',
    image: '/assets/36c87bd9-7bdf-4413-bf74-70043278eb04.png',
    readTime: '7 min'
  }
];

const BlogPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredPosts, setFilteredPosts] = useState(initialBlogPosts);
  
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const term = e.target.value.toLowerCase();
    setSearchTerm(term);
    
    if (term.trim() === '') {
      setFilteredPosts(initialBlogPosts);
    } else {
      const filtered = initialBlogPosts.filter(
        post => 
          post.title.toLowerCase().includes(term) || 
          post.excerpt.toLowerCase().includes(term)
      );
      setFilteredPosts(filtered);
    }
  };
  
  const filterByCategory = (category: string) => {
    if (category === 'all') {
      setFilteredPosts(initialBlogPosts);
    } else {
      const filtered = initialBlogPosts.filter(post => post.category === category);
      setFilteredPosts(filtered);
    }
  };

  return (
    <>
      <SEO 
        title="SaaS Reseller Blog | Insights for Referral Program Entrepreneurs"
        description="Discover expert insights, strategies and tips for growing your SaaS reseller business with our regularly updated blog."
        keywords="saas reseller blog, white label referral program, saas entrepreneur, referral marketing tips"
        ogImage="/assets/5df22fbf-46c0-422b-a964-30ad729c926d.png"
      />
      <div className="min-h-screen flex flex-col">
        <Header isLoggedIn={false} />
        <main className="flex-grow py-8 px-4 bg-gray-50">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h1 className="text-4xl font-extrabold text-gray-900 mb-2">SaaS Reseller Blog</h1>
              <p className="text-xl text-gray-600">Insights, strategies, and best practices for growing your referral program business</p>
            </div>
            
            <div className="flex flex-col md:flex-row gap-8 mb-10">
              <div className="md:w-3/4">
                <div className="mb-8">
                  <Input
                    placeholder="Search articles..."
                    value={searchTerm}
                    onChange={handleSearch}
                    className="max-w-md"
                  />
                </div>
                
                <Tabs defaultValue="all" className="mb-8">
                  <TabsList>
                    <TabsTrigger value="all" onClick={() => filterByCategory('all')}>All</TabsTrigger>
                    <TabsTrigger value="business" onClick={() => filterByCategory('business')}>Business</TabsTrigger>
                    <TabsTrigger value="marketing" onClick={() => filterByCategory('marketing')}>Marketing</TabsTrigger>
                    <TabsTrigger value="trends" onClick={() => filterByCategory('trends')}>Trends</TabsTrigger>
                  </TabsList>
                </Tabs>
                
                <div className="space-y-6">
                  {filteredPosts.length > 0 ? (
                    filteredPosts.map(post => (
                      <Card key={post.id} className="overflow-hidden">
                        <div className="md:flex">
                          <div className="md:w-1/3 h-48 md:h-auto">
                            <img 
                              src={post.image} 
                              alt={post.title} 
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <div className="md:w-2/3">
                            <CardHeader>
                              <div className="flex justify-between items-center mb-2">
                                <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full uppercase font-medium">
                                  {post.category}
                                </span>
                                <span className="text-gray-500 text-sm">{post.date} • {post.readTime} read</span>
                              </div>
                              <CardTitle className="text-xl font-bold text-gray-800 hover:text-primary-blue transition-colors">
                                <Link to={`/blog/${post.id}`}>{post.title}</Link>
                              </CardTitle>
                              <CardDescription className="text-sm text-gray-500">By {post.author}</CardDescription>
                            </CardHeader>
                            <CardContent>
                              <p>{post.excerpt}</p>
                            </CardContent>
                            <CardFooter>
                              <Link to={`/blog/${post.id}`}>
                                <Button variant="ghost">Read More &rarr;</Button>
                              </Link>
                            </CardFooter>
                          </div>
                        </div>
                      </Card>
                    ))
                  ) : (
                    <div className="text-center py-12 bg-white rounded-lg shadow">
                      <p className="text-gray-600 mb-4">No articles found matching your search.</p>
                      <Button onClick={() => { setSearchTerm(''); setFilteredPosts(initialBlogPosts); }}>
                        View All Articles
                      </Button>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="md:w-1/4">
                <Card>
                  <CardHeader>
                    <CardTitle>Subscribe to Updates</CardTitle>
                    <CardDescription>Get the latest articles delivered to your inbox</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <form className="space-y-4">
                      <Input placeholder="Your email address" type="email" />
                      <Button className="w-full">Subscribe</Button>
                    </form>
                  </CardContent>
                </Card>
                
                <Card className="mt-6">
                  <CardHeader>
                    <CardTitle>Popular Topics</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      <Button variant="outline" size="sm" onClick={() => setSearchTerm('referral')}>Referral Programs</Button>
                      <Button variant="outline" size="sm" onClick={() => setSearchTerm('revenue')}>Revenue Sharing</Button>
                      <Button variant="outline" size="sm" onClick={() => setSearchTerm('marketing')}>Marketing</Button>
                      <Button variant="outline" size="sm" onClick={() => setSearchTerm('saas')}>SaaS</Button>
                      <Button variant="outline" size="sm" onClick={() => setSearchTerm('white-label')}>White Label</Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
};

export default BlogPage;
